/**
 * S2 (AB#2205, SG-16) — staff booking notifications rendered in the UI language
 * from `type` + `data` (the server's title / message are an English fallback).
 * Unknown types are not handled here: the bell keeps its current rendering.
 */
import {
    STAFF_BOOKING_NOTIFICATION_TYPES,
    type StaffBookingNotificationData,
    type StaffBookingNotificationType,
} from '../types/staffSelf';
import { fill, formatDayDate, isolate, localizedName } from './staffDisplay';

type TFn = (key: string) => string;

export function isStaffBookingNotificationType(type: unknown): type is StaffBookingNotificationType {
    return typeof type === 'string' && (STAFF_BOOKING_NOTIFICATION_TYPES as readonly string[]).includes(type);
}

/** `data` as an object, whether the API sent an object or its JSON string. */
export function parseStaffBookingData(raw: unknown): StaffBookingNotificationData {
    if (!raw) return {};
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed as StaffBookingNotificationData : {};
        } catch {
            return {};
        }
    }
    return typeof raw === 'object' ? raw as StaffBookingNotificationData : {};
}

function when(date: string | null | undefined, time: string | null | undefined, language: string, t: TFn): string {
    const day = formatDayDate(date, language);
    if (day && time) return fill(t('stf.notif.when'), { date: day, time: isolate(time) });
    return day || (time ? isolate(time) : '');
}

const CANCEL_SOURCE_KEY: Record<string, string> = {
    Customer: 'stf.notif.cancelled.customer',
    Salon: 'stf.notif.cancelled.salon',
    Admin: 'stf.notif.cancelled.salon',
    SalonDeclined: 'stf.notif.cancelled.declined',
    Expired: 'stf.notif.cancelled.expired',
};

export interface StaffNotificationText {
    title: string;
    message: string;
}

/** Localized title + one-line summary of a staff booking notification. */
export function staffBookingNotificationText(
    type: StaffBookingNotificationType,
    rawData: unknown,
    language: string,
    t: TFn,
): StaffNotificationText {
    const data = parseStaffBookingData(rawData);
    const service = localizedName(data.serviceName, data.serviceNameAr, language);
    const branch = localizedName(data.branchName, data.branchNameAr, language);
    const customer = data.customerFirstName ? isolate(data.customerFirstName) : '';
    const now = when(data.date, data.startTime, language, t);

    let title: string;
    let detail = now;
    let reason = '';
    if (type === 'StaffBookingAssigned') {
        title = t('stf.notif.assigned.title');
    } else if (type === 'StaffBookingChanged') {
        if (data.change === 'reassignedAway') {
            title = t('stf.notif.removed.title');
        } else if (data.previousDate || data.previousStartTime) {
            title = t('stf.notif.moved.title');
            const before = when(data.previousDate ?? data.date, data.previousStartTime ?? data.startTime, language, t);
            if (before && now) detail = fill(t('stf.notif.movedFrom'), { now, before });
        } else {
            title = t('stf.notif.changed.title');
        }
    } else {
        title = t('stf.notif.cancelled.title');
        const key = data.source ? CANCEL_SOURCE_KEY[data.source] : undefined;
        if (key) reason = t(key);
    }

    const message = [service, customer, detail, branch, reason].filter(Boolean).join(' · ');
    return { title, message };
}

/**
 * A StaffBookingChanged "reassigned away" notification: the booking now belongs
 * to another staff member, so it must not be opened from this account.
 */
export function isReassignedAway(type: unknown, rawData: unknown): boolean {
    return type === 'StaffBookingChanged' && parseStaffBookingData(rawData).change === 'reassignedAway';
}

/** The booking a staff notification points at (data.appointmentId, else referenceId). */
export function staffNotificationAppointmentId(rawData: unknown, referenceId?: number | null): number | null {
    const id: unknown = parseStaffBookingData(rawData).appointmentId;
    if (typeof id === 'number' && id > 0) return id;
    if (typeof id === 'string' && Number(id) > 0) return Number(id);
    return referenceId && referenceId > 0 ? referenceId : null;
}
